import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { format, parseISO } from 'date-fns';
import _ from 'lodash';
import { useTranslation } from '@/components/utils/translations';
import { isEvasysFamilyTenant, isDemarqueFamilyTenant, isBibliothecaFamilyTenant } from '@/lib/tenantUtils';

// Helper function to safely parse and format dates
const safeParseDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

const safeFormatDate = (dateString, formatStr) => {
  const date = safeParseDate(dateString);
  if (!date) return 'Invalid';
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return 'Invalid';
  }
};

// Helper to sanitize momentum score change values (handles en-dash issue)
const sanitizeMomentumChange = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/–/g, '-').trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

export default function MomentumTrend({ momentumScores, countryFilter = 'all', market }) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isDemarque =
    isDemarqueFamilyTenant(momentumScores?.[0]?.tenant_id) ||
    isBibliothecaFamilyTenant(momentumScores?.[0]?.tenant_id);
  const isCalmedica = momentumScores?.[0]?.tenant_id === 'calmedica';
  const isEdqwest = momentumScores?.[0]?.tenant_id === 'edqwest';
  const isSkillwell = momentumScores?.[0]?.tenant_id === 'skillwell';
  const isKairadigital = momentumScores?.[0]?.tenant_id === 'kairadigital';
  const isWayground = momentumScores?.[0]?.tenant_id === 'wayground';
  const isPlataformaa = momentumScores?.[0]?.tenant_id === 'plataformaa';
  const isEvasys = isEvasysFamilyTenant(momentumScores?.[0]?.tenant_id);
  const isLearnwise = market === 'learnwise';

  // Group by week — only legacy weekly deltas (summable). Institutional signal_score is not a delta.
  const weeklyData = React.useMemo(() => {
    const validScores = (momentumScores || [])
      .filter((s) => s.weekly_period && s.momentum_score_change != null && safeParseDate(s.weekly_period))
      .map((s) => ({
        ...s,
        momentum_score_change: sanitizeMomentumChange(s.momentum_score_change),
      }))
      .filter((s) => s.momentum_score_change != null);

    // Institutional rows have no weekly_period — do not invent a trend by averaging
    // importance scores. Leave weeklyData empty; the card shows a signal list instead.
    if (!validScores.length) return [];

    const tenantIds = new Set(
      validScores
        .map(s => String(s?.tenant_id || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const primaryTenant = tenantIds.size === 1 ? Array.from(tenantIds)[0] : null;
    const MULTI_COUNTRY_TENANTS = new Set(['instructure', 'clairelabs']);
    const shouldAutoConsolidateAllCountries =
      countryFilter === 'all' &&
      Boolean(primaryTenant) &&
      !MULTI_COUNTRY_TENANTS.has(primaryTenant);
    
    if (isDemarque) {
      // For demarque, show average across all
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            'Average': avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isCalmedica) {
      // For calmedica, show France data
      const grouped = _.groupBy(validScores.filter(s => s.country === 'France'), 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            'France': avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isEdqwest) {
      // For edqwest, show average (no country breakdown)
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            'EdQwest': avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isSkillwell) {
      // For skillwell, show single aggregated line (no country breakdown)
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            'All Companies': avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isKairadigital) {
      // For kairadigital, show single aggregated line
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            'Average': avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isWayground) {
      // For wayground, show single aggregated line
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            'Average': avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isPlataformaa) {
      // For plataformaa, show single aggregated line
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            'Average': avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isEvasys) {
      // Evasys: single consolidated curve across all countries (same as demarque / edqwest pattern)
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            Evasys: avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else if (isLearnwise) {
      // For learnwise: all countries = one smooth average line; specific country = that country's line
      if (countryFilter === 'all') {
        const grouped = _.groupBy(validScores, 'weekly_period');
        return Object.entries(grouped)
          .map(([week, scores]) => {
            const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
            return {
              week,
              'Average': avgChange
            };
          })
          .sort((a, b) => new Date(a.week) - new Date(b.week))
          .slice(-12);
      } else {
        const filteredByCountry = validScores.filter(s => s.country && s.country.toLowerCase() === countryFilter.toLowerCase());
        const grouped = _.groupBy(filteredByCountry, 'weekly_period');
        return Object.entries(grouped)
          .map(([week, scores]) => {
            const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
            return {
              week,
              [countryFilter]: avgChange
            };
          })
          .sort((a, b) => new Date(a.week) - new Date(b.week))
          .slice(-12);
      }
    } else if (shouldAutoConsolidateAllCountries) {
      // Tenant-scoped default: on "All Countries", show a single consolidated average line.
      const grouped = _.groupBy(validScores, 'weekly_period');
      return Object.entries(grouped)
        .map(([week, scores]) => {
          const avgChange = _.meanBy(scores, 'momentum_score_change') || 0;
          return {
            week,
            Average: avgChange
          };
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
    } else {
      // For other tenants, group by week and country - NO "All Countries"
      const grouped = _.groupBy(validScores, 'weekly_period');
      const weeklyDataPoints = Object.entries(grouped)
        .map(([week, scores]) => {
          const byCountry = _.groupBy(scores, 'country');
          const result = { week };
          
          // Calculate average for each country
          Object.entries(byCountry).forEach(([country, countryScores]) => {
            if (country && country !== 'undefined' && country !== 'null') {
              const avgChange = _.meanBy(countryScores, 'momentum_score_change') || 0;
              result[country] = avgChange;
            }
          });
          
          return result;
        })
        .sort((a, b) => new Date(a.week) - new Date(b.week))
        .slice(-12);
      
      return weeklyDataPoints;
    }
  }, [momentumScores, isDemarque, isCalmedica, isEdqwest, isSkillwell, isKairadigital, isWayground, isPlataformaa, isEvasys, isLearnwise, countryFilter]);

  const countries = React.useMemo(() => {
    if (isDemarque) {
      return ['Average'];
    }
    if (isCalmedica) {
      return ['France'];
    }
    if (isEdqwest) {
      return ['EdQwest'];
    }
    if (isSkillwell) {
      return ['All Companies'];
    }
    if (isKairadigital) {
      return ['Average'];
    }
    if (isWayground) {
      return ['Average'];
    }
    if (isPlataformaa) {
      return ['Average'];
    }
    if (isEvasys) {
      return ['Evasys'];
    }
    if (isLearnwise) {
      if (countryFilter === 'all') return ['Average'];
      return [countryFilter];
    }
    if (countryFilter === 'all') {
      const tenantIds = new Set(
        (momentumScores || [])
          .map(s => String(s?.tenant_id || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const primaryTenant = tenantIds.size === 1 ? Array.from(tenantIds)[0] : null;
      const MULTI_COUNTRY_TENANTS = new Set(['instructure', 'clairelabs']);
      if (primaryTenant && !MULTI_COUNTRY_TENANTS.has(primaryTenant)) {
        return ['Average'];
      }
    }
    const allCountries = new Set();
    weeklyData.forEach(week => {
      Object.keys(week).forEach(key => {
        if (key !== 'week') allCountries.add(key);
      });
    });
    
    // Sort alphabetically
    return Array.from(allCountries).sort();
  }, [weeklyData, isDemarque, isCalmedica, isEdqwest, isSkillwell, isKairadigital, isWayground, isPlataformaa, isEvasys, isLearnwise, countryFilter, momentumScores]);

  const institutionalSignals = React.useMemo(() => {
    return (momentumScores || [])
      .filter((s) => s.momentum_score_change == null && s.signal_score != null && !Number.isNaN(Number(s.signal_score)))
      .slice()
      .sort((a, b) => Math.abs(Number(b.signal_score)) - Math.abs(Number(a.signal_score)))
      .slice(0, 8);
  }, [momentumScores]);

  const colors = isDemarque 
    ? { 'Average': '#00C2C2' }
    : isCalmedica
    ? { 'France': '#00C2C2' }
    : isEdqwest
    ? { 'EdQwest': '#00C2C2' }
    : isEvasys
    ? { Evasys: '#00C2C2' }
    : isSkillwell
    ? { 'All Companies': '#00C2C2' }
    : isLearnwise
    ? { 'Average': '#00C2C2' }
    : countryFilter === 'all' && countries.length === 1 && countries[0] === 'Average'
    ? { Average: '#00C2C2' }
    : {
        'Australia': '#00C2C2',
        'Hong Kong': '#FF6B6B',
        'South Africa': '#4ECDC4',
        'Canada': '#3FD1FF',
        'United Kingdom': '#9B59B6'
      };

  const getColor = (country) => {
    if (isLearnwise) return colors[country] || '#00C2C2';
    return colors[country] || '#0A1A2F';
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#00C2C2]" />
          <CardTitle className="text-lg font-semibold text-[#0A1A2F]">
            {t('marketMomentumTrend')}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {weeklyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
            <AreaChart 
              data={weeklyData} 
              margin={isMobile 
                ? { top: 10, right: 10, left: 35, bottom: 20 }
                : { top: 5, right: 30, left: 35, bottom: 5 }
              }
            >
              <defs>
                {countries.map((country) => (
                  <linearGradient key={country} id={`color-${country.replace(/\s/g, '-')}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={getColor(country)} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={getColor(country)} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="week" 
                tickFormatter={(value) => safeFormatDate(value, 'MMM d')}
                stroke="#5C6B7A"
                style={{ fontSize: '10px' }}
                tick={{ fill: '#5C6B7A' }}
                interval={isMobile ? 'preserveStartEnd' : 0}
              />
              <YAxis 
                stroke="#5C6B7A" 
                style={{ fontSize: '10px' }}
                domain={['auto', 'auto']}
                width={35}
                tick={{ fill: '#5C6B7A' }}
                tickCount={isMobile ? 5 : 6}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#0A1A2F', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '10px' }}
                labelStyle={{ fontSize: '10px' }}
                labelFormatter={(value) => safeFormatDate(value, 'MMM d, yyyy')}
                formatter={(value, name) => [value >= 0 ? `+${value.toFixed(1)}` : value.toFixed(1), name]}
              />
              {countries.map((country) => (
                <Area
                  key={country}
                  type="monotone"
                  dataKey={country}
                  stroke={getColor(country)}
                  strokeWidth={isMobile ? 3 : 2.5}
                  fill={`url(#color-${country.replace(/\s/g, '-')})`}
                  name={country}
                  dot={false}
                  connectNulls
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        ) : institutionalSignals.length > 0 ? (
          <div className="space-y-2 py-1">
            <p className="text-xs text-[#5C6B7A]">
              {institutionalSignals.length} live {institutionalSignals.length === 1 ? 'signal' : 'signals'} — no weekly trend for this lane.
            </p>
            <ul className="space-y-1.5 max-h-[360px] overflow-y-auto">
              {institutionalSignals.map((s) => {
                const score = Number(s.signal_score);
                return (
                  <li key={s.id || `${s.institution_name}-${s.date_published}`} className="text-sm flex items-start justify-between gap-3">
                    <span className="text-[#0A1A2F] min-w-0">
                      <span className="font-medium">{s.institution_name || 'Account'}</span>
                      {s.summary ? <span className="text-[#5C6B7A]"> — {s.summary}</span> : null}
                    </span>
                    <span className={`shrink-0 tabular-nums font-medium ${score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {score > 0 ? `+${score}` : score}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div style={{ height: isMobile ? 300 : 400 }} className="flex items-center justify-center text-[#5C6B7A]">
            {t('noMomentumData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}